import { Controller } from "@hotwired/stimulus";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

let optionsSet = false;

export default class extends Controller {
  static targets = [
    "address",
    "address2",
    "city",
    "state",
    "zip",
    "fullAddress",
    "additionalAddressFields",
  ];
  static values = { key: String };

  connect() {
    if (!this.keyValue) {
      return;
    }

    if (!optionsSet) {
      setOptions({
        key: this.keyValue,
        v: "weekly",
      });
      optionsSet = true;
    }

    importLibrary("places")
      .then(({ Autocomplete }) => {
        this.initializeAutocomplete(Autocomplete);
      })
      .catch((error) => {
        console.error("Failed to load Google Maps API:", error);
      });
  }

  initializeAutocomplete(Autocomplete) {
    const el = this.hasFullAddressTarget ? this.fullAddressTarget : this.addressTarget;

    this.autocomplete = new Autocomplete(el, {
      types: ["address"],
      componentRestrictions: { country: ["us"] },
      fields: ["address_components", "geometry"],
    });

    this.autocomplete.addListener("place_changed", this.fillInAddress.bind(this));
  }

  showAdditionalAddressFields() {
    if (
      this.hasAdditionalAddressFieldsTarget &&
      !this.additionalAddressFieldsTarget.classList.contains("opacity-100")
    ) {
      this.additionalAddressFieldsTarget.classList.add("opacity-100", "max-h-[1000px]");
    }
  }

  handleAddressInput() {
    if (this.addressTarget.value.trim() !== "") {
      this.showAdditionalAddressFields();
    }
  }

  fillInAddress() {
    const place = this.autocomplete.getPlace();

    if (!place.address_components) {
      return;
    }

    this.showAdditionalAddressFields();

    const components = place.address_components;

    const componentMapping = {
      street_number: "short_name",
      route: "long_name",
      subpremise: "long_name",
      locality: "long_name",
      administrative_area_level_3: "long_name",
      administrative_area_level_2: "long_name",
      sublocality: "long_name",
      sublocality_level_1: "long_name",
      administrative_area_level_1: "short_name",
      postal_code: "short_name",
    };

    const componentForm = {};
    components.forEach((component) => {
      component.types.forEach((type) => {
        if (componentMapping[type] && !componentForm[type]) {
          componentForm[type] = component[componentMapping[type]];
        }
      });
    });

    const cityTypes = [
      "locality",
      "sublocality",
      "sublocality_level_1",
      "administrative_area_level_2",
      "administrative_area_level_3",
    ];

    const getComponent = (types) => {
      for (let i = 0; i < types.length; i++) {
        if (componentForm[types[i]]) {
          return componentForm[types[i]];
        }
      }
      return "";
    };

    const streetNumber = componentForm["street_number"] || "";
    const route = componentForm["route"] || "";
    const subpremise = componentForm["subpremise"] || "";
    const city = getComponent(cityTypes);
    const state = componentForm["administrative_area_level_1"] || "";
    const postalCode = componentForm["postal_code"] || "";

    this.addressTarget.value = `${streetNumber} ${route}`.trim();
    this.address2Target.value = subpremise;
    this.cityTarget.value = city;
    this.stateTarget.value = state;
    this.zipTarget.value = postalCode;

    [
      this.addressTarget,
      this.address2Target,
      this.cityTarget,
      this.stateTarget,
      this.zipTarget,
    ].forEach((target) => {
      if (target.value.trim() !== "") {
        target.classList.add("!border-primary");
        requestAnimationFrame(() => {
          target.classList.add("transition-all", "duration-1000");
          target.classList.remove("!border-primary");
          setTimeout(() => {
            target.classList.remove("transition-all", "duration-1000");
          }, 1000);
        });
      }
    });
  }
}
